import useModal from 'hooks/useModal';
import { BuyerFAQContext, BuyerFAQMeta } from './context';
import useCrud from 'hooks/useCrud';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { queryMercadoFAQList } from '../services';
import dayjs from 'dayjs';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { Drawer, DrawerProps, Space, Switch, Tabs, message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { useMemoizedFn, useUpdateEffect } from 'ahooks';
import { LoadingOutlined } from '@ant-design/icons';
import Item from '../Item';
import { noop } from 'lodash-es';
import { css } from '@emotion/css';
import useTrade from 'views/Conversation/Sider/useTrade';
import { TradeListContext } from 'views/Conversation/Sider/TradeContext';
import TradeList from './TradeList';
import { getTalkInfoByBuyerStore } from 'services/api/refund';
import Empty from 'components/Empty';

export function useBuyerFAQContext() {
  const value = useContext(BuyerFAQContext);

  if (!value) {
    throw new Error(
      'useBuyerFAQContext hook should be used in a context of BuyerFAQProvider component',
    );
  }

  return value;
}

export function BuyerFAQProvider({ children }: { children: JSX.Element }) {
  const { meta, visible, show, hide } = useModal<BuyerFAQMeta>();

  const [autoTranslate, setAutoTranslate] = useState(false);

  return (
    <BuyerFAQContext.Provider
      value={useMemo(
        () => ({
          autoTranslate,
          setAutoTranslate,
          show,
        }),
        [autoTranslate, show],
      )}
    >
      {children}
      {meta && (
        <BuyerFAQDrawer
          key={Object.values(meta).join('@')}
          visible={visible}
          onClose={() => hide()}
          meta={meta}
        />
      )}
    </BuyerFAQContext.Provider>
  );
}

export function BuyerFAQDrawer({
  meta,
  ...otherProps
}: DrawerProps & {
  meta: BuyerFAQMeta;
}) {
  const { t } = useTranslate();

  const { autoTranslate, setAutoTranslate } = useBuyerFAQContext();
  const systemLang = useSystemLanguageValue();
  const lang = autoTranslate ? systemLang : undefined;

  const {
    list: faqList,
    loading: faqListLoading,
    fetchList: fetchFaqList,
  } = useCrud({
    readList: useCallback(
      ({ storeId, buyerId, lang }: BuyerFAQMeta & { lang?: General.SystemLanguage }) => {
        return queryMercadoFAQList({
          pageNo: 1,
          page: 99999,
          orderBy: 'asc',
          storeIds: [storeId],
          buyerId,
          startTime: dayjs().subtract(3, 'months').format('YYYY-MM-DD'),
          endTime: dayjs().format('YYYY-MM-DD'),
          lang,
        }).then((res) => res.data.data.data);
      },
      [],
    ),
  });

  const useTradeRes = useTrade(meta.storeId);
  const { hasMore: hasMoreTrade, fetchList: fetchTradeList } = useTradeRes;
  const [tradeListLoading, setTradeListLoading] = useState(false);
  const [conversation, setConversation] = useState<Conversation.Conversation>();

  const [activeKey, setActiveKey] = useState(meta?.activeKey ?? 'faq');

  const refreshFaqList = useMemoizedFn(() => {
    return fetchFaqList({ ...meta, lang }).catch(() => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    });
  });
  const refreshTradeList = useMemoizedFn(() => {
    setTradeListLoading(true);
    return getTalkInfoByBuyerStore({
      items: [
        {
          channel: 'MERCADO',
          internalStoreId: Number(meta.storeId),
          internalBuyerId: Number(meta.buyerId),
        } as any,
      ],
    })
      .then(({ talkInfos }) => {
        setConversation(talkInfos[0]);
        return fetchTradeList(
          {
            page_no: 1,
            page_size: 999,
            external_buyer_id: meta.buyerId,
            internal_store_id: meta.storeId,
          },
          'MERCADO',
        );
      })
      .catch(() => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      })
      .finally(() => setTradeListLoading(false));
  });

  useEffect(() => {
    if (activeKey === 'faq' && !faqList) {
      refreshFaqList();
    }
    if (activeKey === 'trade' && hasMoreTrade) {
      refreshTradeList();
    }
  }, [activeKey, faqList, hasMoreTrade, refreshFaqList, refreshTradeList]);

  useUpdateEffect(() => {
    refreshFaqList();
  }, [lang]);

  const tabs = (
    <Tabs activeKey={activeKey} onChange={setActiveKey}>
      <Tabs.TabPane key="faq" tab="Q&A" />
      <Tabs.TabPane key="trade" tab={t('Entities.订单')} />
    </Tabs>
  );

  const translateSwitch = activeKey === 'faq' && (
    <Space>
      {t('translation.Translate')}
      <Switch checked={autoTranslate} onChange={setAutoTranslate} />
    </Space>
  );

  const faqContent =
    activeKey === 'faq' ? (
      faqListLoading ? (
        <LoadingOutlined />
      ) : faqList?.length ? (
        <div className={styles.faqContainer}>
          {faqList.map((faqInfo) => {
            return <Item key={faqInfo.id} preview faqInfo={faqInfo} onChange={noop} />;
          })}
        </div>
      ) : (
        <Empty />
      )
    ) : null;

  const tradeContent =
    activeKey === 'trade' ? (
      <TradeListContext.Provider
        value={{
          ...useTradeRes,
          loading: tradeListLoading,
          setLoading: setTradeListLoading,
          conversation,
        }}
      >
        <TradeList />
      </TradeListContext.Provider>
    ) : null;

  return (
    <Drawer
      width={720}
      className={styles.drawer}
      title={tabs}
      extra={translateSwitch}
      {...otherProps}
    >
      {faqContent}
      {tradeContent}
    </Drawer>
  );
}

const styles = {
  drawer: css`
    .ant-drawer-title {
      .ant-tabs {
        margin: -17px 0;
        .ant-tabs-nav {
          margin-bottom: 0;
        }
        .ant-tabs-tab {
          padding: 17px 0;
        }
      }
    }
    .ant-drawer-body {
      padding: 0;
    }
  `,
  faqContainer: css`
    height: 100%;
    padding: 16px;
    background: #f7f9fa;
    overflow: auto;
    > tr:first-of-type > td {
      height: 0;
    }
  `,
};

export default BuyerFAQProvider;
